<template>
  <img
    v-if="profileImage" class="profile-pic" :class="{ size1: size === 1, hover: hover, 'is-safari-v15': isSafariVer15 }" :src="profileImage"
    @error="onProfileImageError"
  >
  <div v-else-if="withPlaceholder" class="profile-pic with-placeholder">
    <img class="img-placeholder" :src="imgPlaceHolder">
  </div>
  <ProfileImageInitials
    v-else
    :initials="initials"
    :font-size="size"
    :initials-bg-color="randomColor"
    :class="{ size1: size === 1, hover: hover, 'is-safari-v15': isSafariVer15 }"
    class="profile-pic"
  />
</template>

<script setup lang="ts">
import { getRandomPastelColor, isSafariVersionOlderThanV16 } from '@/services/utils'
import { capitalize, computed } from 'vue'
import { useStore } from 'vuex'
import ProfileImageInitials from '@/components/common/profile-image/ProfileImageInitials.vue'
import { NOT_AVAILABLE } from '@/services/constants'
import imgPlaceHolder from '@/assets/onboarding/img-placeholder.svg'

interface ProfileImage {
  size?: number
  hover?: boolean
  profileImage?: string
  scalable?: boolean
  customWidthHeight?: string
  withPlaceholder?: boolean
  customOutline?: string
  fallback?: string
}

const store = useStore()

const props = withDefaults(defineProps<ProfileImage>(), {
  size: 2,
  customWidthHeight: '7.5rem',
  customOutline: '3px solid #dadbe8;'
})
const firstName = computed(() => store.state.user.user.first_name)
const lastName = computed(() => store.state.user.user.last_name)
const randomColor = computed(() => store.state.user.user.color ?? getRandomPastelColor())
const initials = computed(() =>
  [firstName.value, lastName.value && lastName.value !== NOT_AVAILABLE ? lastName.value : ''].map((name) => capitalize(name ?? '').slice(0, 1)).join('')
)
const isSafariVer15 = computed(() => isSafariVersionOlderThanV16())

function onProfileImageError(e: Event) {
  if (!props.fallback) return
  const img = e.target as HTMLImageElement
  if (img.src !== props.fallback) {
    img.src = props.fallback
  }
}
</script>

<style lang="scss" scoped>
.profile-pic {
  position: relative;
  width: v-bind('props.customWidthHeight');
  height: v-bind('props.customWidthHeight');
  border-radius: 100%;
  outline: v-bind('props.customOutline');
  object-fit: cover;

  &.with-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $grey-100;
  }

  &.is-safari-v15 {
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      right: -6px;
      bottom: -6px;
      left: -6px;
      border: v-bind('props.customOutline');
      border-radius: 100%;
    }
  }

  @media (max-width: $sm) {
    width: 70px;
    height: 70px;
  }

  &.size1 {
    outline: none;
    box-shadow: none;
    &.is-old-safari {
      &::before {
        content: none;
      }
    }
  }

  &.hover:hover {
    transform: scale(1.05);
  }
}
</style>
